<template>
    <section class="ftv-root icp-page min-h-screen w-full">
        <div class="icp-page-bg" aria-hidden="true"></div>
        <div class="relative container mx-auto px-4 sm:px-8 py-8 sm:py-12">
            <div class="flex flex-col h-full w-full">
                <nav class="icp-breadcrumb text-sm mb-8 flex flex-wrap items-center">
                    <router-link to="/dashboard" class="icp-breadcrumb-link">Dashboard</router-link>
                    <span class="icp-breadcrumb-sep mx-2">/</span>
                    <span class="icp-breadcrumb-current">Internship / Clinical Placement</span>
                </nav>

                <header class="icp-hero mb-10 sm:mb-14">
                    <p class="icp-hero-kicker">Career Development</p>
                    <h1 class="icp-hero-title">Internship / Clinical Placement</h1>
                    <p class="icp-hero-lead">
                        Browse available opportunities for your entity and campus.
                    </p>
                </header>

                <!-- List -->
                <div class="ftv-list-panel">
                    <div class="ftv-tabs-row">
                        <div class="ftv-tabs" role="tablist" aria-label="Filter opportunities by enrollment status">
                            <button v-for="tab in statusTabs" :key="tab.value" type="button" role="tab"
                                class="ftv-tab" :class="{ 'ftv-tab-active': activeStatus === tab.value }"
                                :aria-selected="activeStatus === tab.value" @click="selectStatus(tab.value)">
                                <span class="ftv-tab-dot" :class="'ftv-tab-dot-' + tab.value"></span>
                                {{ tab.label }}
                            </button>
                        </div>
                        <div v-if="paginatorInfo" class="ftv-result-count">
                            <span class="ftv-result-count-value">{{ paginatorInfo.total }}</span>
                            {{ paginatorInfo.total === 1 ? 'opportunity' : 'opportunities' }}
                        </div>
                    </div>

                    <div v-if="loading" class="flex justify-center items-center py-12">
                        <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                    </div>

                    <div v-else-if="items.length === 0" class="ftv-empty-state">
                        <div class="ftv-empty-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6"
                                    d="M20 13V7a2 2 0 00-2-2h-3.5l-1.2-1.5a2 2 0 00-1.56-.75H6a2 2 0 00-2 2V13m16 0v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4m16 0h-5l-1.5 2h-3L9 13H4" />
                            </svg>
                        </div>
                        <div class="ftv-empty-title">No {{ activeStatusLabel.toLowerCase() }} opportunities found</div>
                        <div class="ftv-empty-copy">{{ emptyStateMessage }}</div>
                    </div>

                    <div v-else class="mt-6">
                        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
                            <article v-for="item in items" :key="item.token" class="ftv-card">
                                <div class="ftv-card-accent" aria-hidden="true"></div>
                                <div class="ftv-card-main">
                                    <div class="ftv-card-top">
                                        <div class="ftv-card-chips">
                                            <span v-if="item.opportunity_type" class="ftv-chip ftv-chip-type">
                                                {{ formatOpportunityType(item.opportunity_type) }}
                                            </span>
                                        </div>
                                        <span v-if="item.viewer_has_enrolled" class="ftv-chip ftv-status-chip"
                                            :class="enrollmentStatusChipClass(item.viewer_enrollment_status)">
                                            {{ formatEnrollmentStatus(item.viewer_enrollment_status) }}
                                        </span>
                                        <span v-else class="ftv-chip ftv-status-chip ftv-chip-open">Open</span>
                                    </div>

                                    <h3 class="ftv-card-title" :title="item.title">{{ item.title }}</h3>

                                    <div class="ftv-desc-box">
                                        <p v-if="item.description" class="ftv-card-desc">{{ item.description }}</p>
                                        <p v-else class="ftv-card-desc ftv-card-desc-muted">
                                            No description has been added for this opportunity yet.
                                        </p>
                                    </div>

                                    <div class="ftv-info-grid">
                                        <div class="ftv-info-block">
                                            <span class="ftv-info-icon" aria-hidden="true">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"
                                                        d="M12 21s6-5.1 6-11a6 6 0 10-12 0c0 5.9 6 11 6 11z" />
                                                    <circle cx="12" cy="10" r="2" stroke-width="1.8" />
                                                </svg>
                                            </span>
                                            <span class="ftv-info-content">
                                                <span class="ftv-info-label">Campus</span>
                                                <span class="ftv-info-value" :title="displayCampuses(item)">
                                                    {{ displayCampuses(item) || 'All campuses' }}
                                                </span>
                                            </span>
                                        </div>
                                        <div class="ftv-info-block">
                                            <span class="ftv-info-icon" aria-hidden="true">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8"
                                                        d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2m7-10a4 4 0 100-8 4 4 0 000 8zm8 0v6m3-3h-6" />
                                                </svg>
                                            </span>
                                            <span class="ftv-info-content">
                                                <span class="ftv-info-label">Seats</span>
                                                <span class="ftv-info-value">
                                                    <template v-if="item.student_count === null">Unlimited</template>
                                                    <template v-else>{{ item.student_count }}</template>
                                                </span>
                                            </span>
                                        </div>
                                    </div>

                                    <div class="ftv-card-context">
                                        <span class="ftv-entity-badge">{{ formatEntityName(item.entity) }}</span>
                                        <span class="ftv-posted-date">Posted {{ formatDate(item.created_at) }}</span>
                                    </div>

                                    <div class="ftv-card-actions">
                                        <div class="ftv-creator" :title="formatCreatedBy(item)">
                                            <span class="ftv-creator-avatar">{{ creatorInitial(item) }}</span>
                                            <span class="ftv-creator-copy">
                                                <span class="ftv-creator-label">Created by</span>
                                                <span class="ftv-creator-name">{{ formatCreatedBy(item) }}</span>
                                            </span>
                                        </div>
                                        <button
                                            v-if="item.viewer_has_enrolled && item.viewer_enrollment_status === 'rejected'"
                                            type="button" class="ftv-btn-resubmit" @click="onOpen(item)">
                                            Resubmit
                                        </button>
                                        <button v-else-if="!item.viewer_has_enrolled" type="button"
                                            class="ftv-btn-enroll" :disabled="enrollBusyFor(item)"
                                            @click="onEnroll(item)">
                                            {{ enrollBusyFor(item) ? 'Enrolling…' : 'Enroll' }}
                                        </button>
                                        <button type="button" class="ftv-btn-primary" @click="onOpen(item)">
                                            View details
                                        </button>
                                    </div>
                                </div>
                            </article>
                        </div>

                        <div v-if="paginatorInfo && paginatorInfo.lastPage > 1" class="ftv-pagination-wrap">
                            <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import { formatEntityName, formatOpportunityType } from "../../helpers/utils";

export default {
    name: "internship-clinical-placement-user-view",
    components: {
        Pagination,
    },
    data() {
        return {
            loading: false,
            opportunities: null,
            first: 9,
            page: 1,
            enrollingToken: null,
            activeStatus: 'all',
            statusTabs: [
                { label: 'All', value: 'all' },
                { label: 'Pending', value: 'pending' },
                { label: 'Approved', value: 'approved' },
                { label: 'Rejected', value: 'rejected' },
            ],
        };
    },
    computed: {
        studentStatus() {
            return this.$store.getters.studentStatus || '';
        },
        paginatorInfo() {
            return this.opportunities && this.opportunities.paginatorInfo ? this.opportunities.paginatorInfo : null;
        },
        items() {
            return this.opportunities && this.opportunities.data ? this.opportunities.data : [];
        },
        activeStatusLabel() {
            const tab = this.statusTabs.find(item => item.value === this.activeStatus);
            return tab ? tab.label : 'All';
        },
        emptyStateMessage() {
            if (this.activeStatus === 'all') {
                return 'New opportunities matching your entity and campus will appear here.';
            }
            return 'You do not have any opportunities with this enrollment status.';
        },
    },
    mounted() {
        this.ensurePermissionAndFetch();
    },
    methods: {
        formatEntityName,
        formatOpportunityType,
        formatCampuses(value) {
            if (!value) return '';
            if (Array.isArray(value)) {
                return value.join(', ');
            }
            return String(value);
        },
        displayCampuses(item) {
            if (!item || !item.campus_code) {
                return '';
            }
            const s = this.formatCampuses(item.campus_code);
            return s ? s.trim() : '';
        },
        hasPermission(name) {
            const permissions = this.$store.getters.permissions || '';
            const permissionsArray = permissions.split(",");
            return permissionsArray.includes(name);
        },
        ensurePermissionAndFetch() {
            const role = this.$store.getters.role || '';
            const isSuperAdmin = this.$store.getters.isSuperAdmin;

            // Page is dedicated for student role 'user', but allow superadmin.
            if (role !== 'user' && !isSuperAdmin) {
                this.$router.replace('/no-permission');
                return;
            }

            // Permission check should not exclude superadmin.
            if (!this.hasPermission('internship') && !isSuperAdmin) {
                this.$router.replace('/no-permission');
                return;
            }

            // Student status gate should apply to student users only.
            if (role === 'user' && this.studentStatus !== 'AS') {
                this.$router.replace('/no-permission');
                return;
            }

            this.fetchOpportunities();
        },
        setPage(page) {
            this.page = page;
            this.fetchOpportunities();
        },
        selectStatus(status) {
            if (status === this.activeStatus || this.loading) {
                return;
            }
            this.activeStatus = status;
            this.page = 1;
            this.fetchOpportunities();
        },
        formatDate(value) {
            if (!value) return '';
            const s = String(value);
            // Prefer YYYY-MM-DD if present, otherwise return trimmed string.
            return s.length >= 10 ? s.substring(0, 10) : s;
        },
        formatEnrollmentStatus(status) {
            const s = status ? String(status) : 'pending';
            if (s === 'approved') {
                return 'Approved';
            }
            if (s === 'rejected') {
                return 'Rejected';
            }
            return 'Pending';
        },
        enrollmentStatusChipClass(status) {
            const s = status ? String(status) : 'pending';
            if (s === 'approved') {
                return 'ftv-chip-approved';
            }
            if (s === 'rejected') {
                return 'ftv-chip-rejected';
            }
            return 'ftv-chip-pending';
        },
        formatCreatedBy(item) {
            const u = item && item.created_by_user ? item.created_by_user : null;
            if (!u || !u.name) {
                return 'Unknown';
            }
            const name = String(u.name).trim();
            return name ? name : 'Unknown';
        },
        creatorInitial(item) {
            const name = this.formatCreatedBy(item);
            return name && name !== 'Unknown' ? name.charAt(0).toUpperCase() : '?';
        },
        onOpen(item) {
            const t = item && item.token ? item.token : '';
            if (!t) {
                return;
            }
            this.$router.push({ path: '/internship-clinical-placement/' + t });
        },
        enrollBusyFor(item) {
            const tok = item && item.token ? item.token : '';
            return tok && this.enrollingToken === tok;
        },
        notifyToast(icon, title) {
            if (this.$swal && typeof this.$swal.mixin === 'function') {
                const Toast = this.$swal.mixin({
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3200,
                    timerProgressBar: true,
                });
                Toast.fire({ icon: icon, title: title });
                return;
            }
            window.alert(title);
        },
        async onEnroll(item) {
            const tok = item && item.token ? item.token : '';
            if (!tok || item.viewer_has_enrolled) {
                return;
            }
            this.enrollingToken = tok;
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation EnrollInternshipClinicalPlacementOpportunity($token: String!) {
                            enrollInternshipClinicalPlacementOpportunity(token: $token) {
                                error
                                error_code
                                message
                                already_enrolled
                            }
                        }
                    `,
                    variables: { token: tok },
                });
                const payload = result && result.data && result.data.enrollInternshipClinicalPlacementOpportunity;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : 'Enrollment could not be completed.';
                    this.notifyToast('error', msg);
                    return;
                }
                const msg = payload.message ? payload.message : 'Success.';
                if (payload.already_enrolled) {
                    this.notifyToast('info', msg);
                } else {
                    this.notifyToast('success', msg);
                }
                await this.fetchOpportunities();
            } catch (e) {
                this.notifyToast('error', 'Enrollment failed. Please try again.');
            } finally {
                this.enrollingToken = null;
            }
        },
        async fetchOpportunities() {
            this.loading = true;
            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query InternshipClinicalPlacementOpportunitiesForStudent(
                            $first: Int!
                            $page: Int!
                            $enrollmentStatus: String
                        ) {
                            internshipClinicalPlacementOpportunitiesForStudent(
                                first: $first
                                page: $page
                                enrollment_status: $enrollmentStatus
                            ) {
                                error
                                error_code
                                message
                                paginatorInfo {
                                    count
                                    currentPage
                                    firstItem
                                    hasMorePages
                                    lastItem
                                    lastPage
                                    perPage
                                    total
                                }
                                data {
                                    id
                                    token
                                    entity
                                    campus_code
                                    opportunity_type
                                    title
                                    description
                                    student_count
                                    viewer_has_enrolled
                                    viewer_enrollment_id
                                    viewer_enrollment_status
                                    created_at
                                    created_by_user {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        first: this.first,
                        page: this.page,
                        enrollmentStatus: this.activeStatus === 'all' ? null : this.activeStatus,
                    },
                    fetchPolicy: 'network-only',
                });

                const payload = result && result.data && result.data.internshipClinicalPlacementOpportunitiesForStudent;
                if (!payload || payload.error) {
                    if (payload && payload.error_code === 403) {
                        this.$router.replace('/no-permission');
                        return;
                    }
                    if (payload && payload.error_code === 401) {
                        this.$router.replace('/');
                        return;
                    }
                    this.opportunities = { data: [], paginatorInfo: { firstItem: 0, lastItem: 0, total: 0, lastPage: 1 } };
                    return;
                }

                this.opportunities = payload;
            } catch (e) {
                console.error('Fetch opportunities failed:', e);
                this.opportunities = { data: [], paginatorInfo: { firstItem: 0, lastItem: 0, total: 0, lastPage: 1 } };
            } finally {
                this.loading = false;
            }
        },
    },
}
</script>

<style scoped>
.icp-page {
    position: relative;
    overflow: hidden;
}

.icp-page-bg {
    position: absolute;
    inset: 0;
    z-index: 0;
    background:
        radial-gradient(ellipse 80% 50% at 10% -10%, rgba(37, 99, 235, 0.12), transparent 55%),
        radial-gradient(ellipse 60% 40% at 90% 10%, rgba(14, 116, 144, 0.1), transparent 50%),
        linear-gradient(165deg, #f1f5f9 0%, #e8eef6 45%, #f0f7fa 100%);
}

.icp-page-bg::after {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.35;
    background-image:
        linear-gradient(rgba(15, 23, 42, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(15, 23, 42, 0.03) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
}

.icp-breadcrumb-link {
    color: #64748b;
    font-weight: 500;
    transition: color 0.2s ease;
}

.icp-breadcrumb-link:hover {
    color: #1e3a8a;
}

.icp-breadcrumb-sep {
    color: #94a3b8;
}

.icp-breadcrumb-current {
    color: #0f172a;
    font-weight: 600;
}

.icp-hero-kicker {
    margin: 0 0 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #0369a1;
}

.icp-hero-title {
    margin: 0 0 0.75rem;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 2.25rem;
    line-height: 1.15;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
}

.icp-hero-lead {
    margin: 0;
    max-width: 42rem;
    font-size: 1.0625rem;
    line-height: 1.65;
    color: #475569;
}

@media (min-width: 768px) {
    .icp-hero-title {
        font-size: 2.75rem;
    }
}

@media (max-width: 639px) {
    .icp-hero-title {
        font-size: 1.875rem;
    }
}

/* Opportunity card — clean professional layout */
.ftv-card {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    border-radius: 18px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 28px rgba(15, 23, 42, 0.05);
    transition: box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease;
    min-width: 0;
    width: 100%;
}

.ftv-card:hover {
    transform: translateY(-2px);
    border-color: #cbd5e1;
    box-shadow: 0 4px 6px rgba(15, 23, 42, 0.04), 0 18px 36px rgba(30, 58, 138, 0.1);
}

.ftv-card-stripe {
    width: 4px;
    flex-shrink: 0;
    background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 55%, #1e3a8a 100%);
}

.ftv-card-main {
    flex: 1;
    min-width: 0;
    padding: 20px 22px 18px;
    display: flex;
    flex-direction: column;
}

.ftv-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.ftv-card-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.ftv-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1.25;
    padding: 5px 11px;
    border: 1px solid transparent;
    white-space: nowrap;
}

.ftv-chip-type {
    background: #ecfdf5;
    border-color: #a7f3d0;
    color: #047857;
}

.ftv-chip-entity {
    background: #f1f5f9;
    border-color: #e2e8f0;
    color: #334155;
}

.ftv-status-chip {
    flex-shrink: 0;
}

.ftv-card-title {
    margin: 0 0 14px 0;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.35;
    letter-spacing: -0.02em;
    color: #0f172a;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}

.ftv-info-row {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 20px;
    margin-bottom: 14px;
}

.ftv-info-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.ftv-info-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
}

.ftv-info-value {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 220px;
}

.ftv-desc-box {
    flex: 1;
    margin-bottom: 14px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    min-height: 64px;
}

.ftv-card-desc {
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: #475569;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}

.ftv-card-desc-muted {
    color: #94a3b8;
    font-style: italic;
}

.ftv-meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    padding-top: 12px;
    margin-bottom: 14px;
    border-top: 1px solid #f1f5f9;
    min-width: 0;
}

.ftv-meta-item {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    min-width: 0;
}

.ftv-meta-item-grow {
    flex: 1;
    min-width: 0;
}

.ftv-meta-dot {
    color: #cbd5e1;
    font-weight: 700;
    flex-shrink: 0;
}

.ftv-meta-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    flex-shrink: 0;
}

.ftv-meta-value {
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
}

.ftv-card-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: auto;
}

.ftv-chip-enrolled {
    background: #ecfdf5;
    border-color: #6ee7b7;
    color: #047857;
    font-weight: 700;
}

.ftv-chip-pending {
    background: #fffbeb;
    border-color: #fcd34d;
    color: #b45309;
    font-weight: 700;
}

.ftv-chip-approved {
    background: #ecfdf5;
    border-color: #6ee7b7;
    color: #047857;
    font-weight: 700;
}

.ftv-chip-rejected {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #b91c1c;
    font-weight: 700;
}

.ftv-btn-enroll,
.ftv-btn-resubmit,
.ftv-btn-primary {
    flex-shrink: 0;
    cursor: pointer;
    border-radius: 10px;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    transition: background 150ms ease, border-color 150ms ease, color 150ms ease, box-shadow 150ms ease, transform 150ms ease, opacity 150ms ease;
}

.ftv-btn-enroll {
    color: #047857;
    background: #ffffff;
    border: 1px solid #10b981;
}

.ftv-btn-enroll:hover:not(:disabled) {
    background: #ecfdf5;
    border-color: #059669;
    color: #065f46;
    transform: translateY(-1px);
}

.ftv-btn-enroll:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.ftv-btn-resubmit {
    color: #1d4ed8;
    background: #ffffff;
    border: 1px solid #2563eb;
}

.ftv-btn-resubmit:hover {
    background: #eff6ff;
    border-color: #1d4ed8;
    color: #1e40af;
    transform: translateY(-1px);
}

.ftv-btn-primary {
    border: none;
    color: #ffffff;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(37, 99, 235, 0.3);
}

.ftv-btn-primary:hover {
    background: linear-gradient(180deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.1), 0 8px 18px rgba(37, 99, 235, 0.35);
    transform: translateY(-1px);
}

.ftv-btn-primary:active {
    transform: translateY(0);
}

/* Student opportunity catalogue */
.ftv-list-panel {
    padding: 24px;
    border: 1px solid #e2e8f0;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.06);
}

.ftv-tabs-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e2e8f0;
}

.ftv-tabs {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px;
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    background: #f8fafc;
}

.ftv-tab {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 38px;
    padding: 8px 14px;
    border: 0;
    border-radius: 10px;
    color: #64748b;
    background: transparent;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
    transition: color 160ms ease, background 160ms ease, box-shadow 160ms ease;
}

.ftv-tab:hover {
    color: #1e40af;
    background: #eff6ff;
}

.ftv-tab-active {
    color: #1d4ed8;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(37, 99, 235, 0.08);
}

.ftv-tab-dot {
    width: 7px;
    height: 7px;
    flex-shrink: 0;
    border-radius: 9999px;
    background: #94a3b8;
}

.ftv-tab-dot-pending {
    background: #f59e0b;
}

.ftv-tab-dot-approved {
    background: #10b981;
}

.ftv-tab-dot-rejected {
    background: #ef4444;
}

.ftv-result-count {
    flex-shrink: 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 600;
}

.ftv-result-count-value {
    color: #0f172a;
    font-size: 15px;
    font-weight: 800;
}

.ftv-card {
    position: relative;
    display: block;
    overflow: hidden;
    min-height: 430px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.03), 0 12px 30px rgba(15, 23, 42, 0.06);
}

.ftv-card:hover {
    transform: translateY(-4px);
    border-color: #bfdbfe;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07), 0 22px 42px rgba(37, 99, 235, 0.12);
}

.ftv-card-accent {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #2563eb 0%, #06b6d4 100%);
}

.ftv-card-main {
    height: 100%;
    padding: 24px 22px 20px;
}

.ftv-card-top {
    margin-bottom: 16px;
}

.ftv-chip {
    padding: 5px 10px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.045em;
    text-transform: uppercase;
}

.ftv-chip-type {
    border-color: #bae6fd;
    color: #0369a1;
    background: #f0f9ff;
}

.ftv-chip-open {
    border-color: #bfdbfe;
    color: #1d4ed8;
    background: #eff6ff;
}

.ftv-card-title {
    min-height: 50px;
    margin-bottom: 10px;
    color: #0f172a;
    font-size: 19px;
    line-height: 1.35;
}

.ftv-desc-box {
    min-height: 68px;
    margin-bottom: 18px;
    padding: 0;
    border: 0;
    background: transparent;
}

.ftv-card-desc {
    color: #64748b;
    font-size: 13px;
    line-height: 1.65;
    -webkit-line-clamp: 3;
    line-clamp: 3;
}

.ftv-info-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(95px, 0.55fr);
    gap: 10px;
    margin-bottom: 16px;
}

.ftv-info-block {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 11px;
    border: 1px solid #e8eef7;
    border-radius: 12px;
    background: #f8fafc;
}

.ftv-info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 9px;
    color: #2563eb;
    background: #eaf2ff;
}

.ftv-info-icon svg {
    width: 16px;
    height: 16px;
}

.ftv-info-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.ftv-info-label {
    color: #94a3b8;
    font-size: 9px;
    font-weight: 800;
}

.ftv-info-value {
    max-width: 100%;
    color: #334155;
    font-size: 12px;
    font-weight: 700;
}

.ftv-card-context {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 16px;
}

.ftv-entity-badge {
    overflow: hidden;
    padding: 4px 9px;
    border-radius: 8px;
    color: #475569;
    background: #f1f5f9;
    font-size: 10px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ftv-posted-date {
    flex-shrink: 0;
    color: #94a3b8;
    font-size: 10px;
    font-weight: 600;
}

.ftv-card-actions {
    align-items: stretch;
    justify-content: stretch;
    padding-top: 16px;
    border-top: 1px solid #eef2f7;
}

.ftv-creator {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    min-width: 0;
    margin-bottom: 2px;
}

.ftv-creator-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 9999px;
    color: #ffffff;
    background: linear-gradient(135deg, #2563eb, #1e40af);
    font-size: 11px;
    font-weight: 800;
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.22);
}

.ftv-creator-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.ftv-creator-label {
    color: #94a3b8;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.ftv-creator-name {
    overflow: hidden;
    color: #475569;
    font-size: 11px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ftv-card-actions .ftv-btn-enroll,
.ftv-card-actions .ftv-btn-resubmit,
.ftv-card-actions .ftv-btn-primary {
    flex: 1 1 120px;
    min-height: 40px;
    padding: 9px 12px;
}

.ftv-pagination-wrap {
    padding-top: 26px;
}

.ftv-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 20px;
    text-align: center;
}

.ftv-empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    border-radius: 20px;
    color: #64748b;
    background: #f1f5f9;
}

.ftv-empty-icon svg {
    width: 30px;
    height: 30px;
}

.ftv-empty-title {
    color: #1e293b;
    font-size: 16px;
    font-weight: 800;
}

.ftv-empty-copy {
    max-width: 420px;
    margin-top: 6px;
    color: #64748b;
    font-size: 13px;
}

@media (max-width: 767px) {
    .ftv-list-panel {
        padding: 16px;
        border-radius: 20px;
    }

    .ftv-tabs-row {
        align-items: flex-start;
        flex-direction: column;
    }

    .ftv-tabs {
        width: 100%;
    }

    .ftv-tab {
        flex: 1 0 auto;
        justify-content: center;
    }
}
</style>
